import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { RANGEMENT_SERVICE, SERVICES } from "../../lib/content";
import { Field } from "./Field";
import { PhotoPicker } from "./PhotoPicker";
import { QuoteAddress } from "./QuoteAddress";
import { addressErrors } from "../../lib/npa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nom: "", entreprise: "", telephone: "", email: "", service: "", message: "", website: "",
  street: "", npa: "", city: "", access: "", billDiff: false, billName: "", billStreet: "", billNpa: "", billCity: "" };
const ADDR_KEYS = ["street", "npa", "city", "access", "billDiff", "billName", "billStreet", "billNpa", "billCity"];

const validate = (v) => {
  const e = {};
  if (v.nom.trim().length < 2) e.nom = v.nom.trim() ? "Le nom doit contenir au moins 2 caractères." : "Veuillez indiquer votre nom.";
  const digits = v.telephone.replace(/\D/g, "");
  if (!v.telephone.trim()) e.telephone = "Veuillez indiquer votre numéro de téléphone.";
  else if (!/^[+\d\s().-]+$/.test(v.telephone) || digits.length < 9 || digits.length > 15)
    e.telephone = "Numéro de téléphone invalide (ex. 021 123 45 67).";
  if (!v.email.trim()) e.email = "Veuillez indiquer votre adresse email.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = "Adresse email invalide.";
  if (v.message.length > 3000) e.message = "Le message ne doit pas dépasser 3000 caractères.";
  Object.assign(e, addressErrors(v));
  if (v.billDiff) Object.assign(e, addressErrors({ street: v.billStreet, npa: v.billNpa, city: v.billCity }, "bill_"));
  return Object.fromEntries(Object.entries(e).map(([k, m]) => [k.replace("bill_street", "billStreet").replace("bill_npa", "billNpa").replace("bill_city", "billCity"), m]));
};

export const QuoteForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [photos, setPhotos] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const uploading = photos.some((p) => p.status === "uploading");

  useEffect(() => {
    const pick = (ev) => setValues((v) => ({ ...v, service: ev.detail }));
    window.addEventListener("cs:preselect-service", pick);
    return () => window.removeEventListener("cs:preselect-service", pick);
  }, []);

  const set = (k) => (ev) => {
    setValues((v) => ({ ...v, [k]: ev.target.value }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const setValue = (k, val) => {
    setValues((v) => ({ ...v, [k]: val }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(values);
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(Object.keys(e)[0].replace(/^bill([A-Z])/, (_, c) => `bill-${c.toLowerCase()}`))?.focus();
      return;
    }
    setStatus("loading");
    try {
      const photo_ids = photos.filter((p) => p.status === "done").map((p) => p.id);
      const base = Object.fromEntries(Object.entries(values).filter(([k]) => !ADDR_KEYS.includes(k)));
      const adresse = { street: values.street.trim(), npa: values.npa.trim(), city: values.city.trim(), notes: values.access.trim() };
      const facturation = values.billDiff ? { name: values.billName.trim(), street: values.billStreet.trim(), npa: values.billNpa.trim(), city: values.billCity.trim() } : null;
      await axios.post(`${API}/quotes`, { ...base, email: values.email.trim(), service: values.service || null, photo_ids, adresse, facturation });
      setStatus("success");
      setValues(EMPTY);
      setPhotos([]);
    } catch (err) {
      setErrMsg(err.response?.status === 429 ? err.response.data.detail : err.response?.status === 422 ? "Certaines informations sont invalides : vérifiez l'adresse (rue, NPA à 4 chiffres, localité)." : "");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="quote-success" className="flex h-full flex-col items-start justify-center rounded-2xl bg-white p-8 md:p-10">
        <CheckCircle2 className="h-12 w-12 text-emerald-600" />
        <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-cs-navy">Merci, votre demande a bien été envoyée !</h3>
        <p className="mt-3 text-[15px] text-cs-ink/75">Nous vous recontactons sous 48 h avec un devis gratuit et sans engagement.</p>
        <button type="button" data-testid="quote-new-request" onClick={() => setStatus("idle")} className="btn-primary mt-8 px-6 py-3 text-sm">
          Envoyer une nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={submit} data-testid="quote-form" className="relative rounded-2xl bg-white p-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)] sm:p-8 md:p-10">
      <h3 className="font-display text-2xl font-bold tracking-tight text-cs-navy">Demande de devis gratuit</h3>
      <p className="mt-1.5 text-sm text-cs-ink/60">Réponse sous 48 h. Les champs marqués * sont obligatoires.</p>
      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field id="nom" label="Nom *" value={values.nom} onChange={set("nom")} error={errors.nom} autoComplete="name" />
        <Field id="entreprise" label="Entreprise (facultatif)" value={values.entreprise} onChange={set("entreprise")} autoComplete="organization" />
        <Field id="telephone" label="Téléphone *" type="tel" value={values.telephone} onChange={set("telephone")} error={errors.telephone} autoComplete="tel" />
        <Field id="email" label="Email *" type="email" value={values.email} onChange={set("email")} error={errors.email} autoComplete="email" />
        <Field id="service" label="Type de service" as="select" value={values.service} onChange={set("service")} className="sm:col-span-2">
          <option value="">Sélectionnez un service</option>
          {SERVICES.map((s) => (
            <option key={s.title} value={s.title}>{s.title}</option>
          ))}
        </Field>
        <Field id="message" label="Message" as="textarea" rows={4} value={values.message} onChange={set("message")} error={errors.message} className="sm:col-span-2" placeholder={values.service === RANGEMENT_SERVICE ? "Pièces à ranger (dressing, cuisine, cave…), disponibilités…" : "Surface, fréquence souhaitée, délais…"} />
        <QuoteAddress values={values} setValue={setValue} errors={errors} />
        <PhotoPicker photos={photos} setPhotos={setPhotos} website={values.website} />
        <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
          <label htmlFor="website">Site web (laisser vide)</label>
          <input id="website" name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} data-testid="quote-honeypot" />
        </div>
      </div>
      {status === "error" && (
        <p data-testid="quote-error" role="alert" className="mt-5 rounded-md bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {errMsg || "Une erreur est survenue lors de l'envoi. Veuillez réessayer."}
        </p>
      )}
      <button type="submit" disabled={status === "loading" || uploading} data-testid="quote-submit-button" className="btn-accent mt-7 w-full py-4 text-base disabled:opacity-70">
        {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : uploading ? "Envoi des photos…" : "Envoyer ma demande"}
      </button>
      <p data-testid="quote-consent-note" className="mt-4 text-center text-[13px] text-cs-ink/60">
        En envoyant ce formulaire, vous acceptez notre{" "}
        <Link to="/confidentialite" data-testid="quote-privacy-link" className="font-semibold text-cs-navy underline decoration-cs-yellow decoration-2 underline-offset-2 hover:text-cs-yellow-dark">
          politique de confidentialité
        </Link>
        .
      </p>
    </form>
  );
};
